import axios from 'axios';
import {makeAutoObservable, runInAction} from 'mobx';
import {api} from './api/endpoints';
import {isConnectivityError} from './utils/offline/offline.cache';

const API_BASE_URL = process.env.REACT_APP_API || '';
const API_PING_INTERVAL_MS = 15000;
const API_PING_TIMEOUT_MS = 8000;
const API_FAILURES_BEFORE_OFFLINE = 2;
const API_RECOVERY_DELAY_MS = 1800;

class NetworkStore {
    public browserOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
    public apiOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
    public lastCheckedAt = '';

    private monitorStarted = false;
    private pollId: number | null = null;
    private probing = false;
    private consecutiveConnectivityFailures = 0;
    private pendingOnlineTimer: number | null = null;

    constructor() {
        makeAutoObservable(this, {}, {autoBind: true});
    }

    public init = () => {
        if (this.monitorStarted || typeof window === 'undefined') {
            return;
        }

        this.monitorStarted = true;
        this.browserOnline = navigator.onLine;
        this.apiOnline = navigator.onLine;

        window.addEventListener('online', this.handleBrowserOnline);
        window.addEventListener('offline', this.handleBrowserOffline);

        this.pollId = window.setInterval(() => {
            void this.checkApiConnection();
        }, API_PING_INTERVAL_MS);

        void this.checkApiConnection();
    };

    public dispose = () => {
        if (typeof window === 'undefined') {
            return;
        }

        window.removeEventListener('online', this.handleBrowserOnline);
        window.removeEventListener('offline', this.handleBrowserOffline);

        if (this.pollId !== null) {
            window.clearInterval(this.pollId);
            this.pollId = null;
        }

        this.monitorStarted = false;
    };

    public get isOfflineMode() {
        return !this.browserOnline || !this.apiOnline;
    }

    public isTrackedApiUrl = (url?: string) => {
        return Boolean(url && API_BASE_URL && url.startsWith(API_BASE_URL));
    };

    public handleApiSuccess = (url?: string) => {
        if (!this.isTrackedApiUrl(url)) {
            return;
        }

        this.registerReachabilitySuccess();
    };

    public handleApiError = (error: any) => {
        const url = error?.config?.url;

        if (!this.isTrackedApiUrl(url) || error?.config?.__networkProbe) {
            return;
        }

        if (isConnectivityError(error)) {
            this.registerReachabilityFailure();
            void this.checkApiConnection();
            return;
        }

        this.registerReachabilitySuccess();
    };

    public checkApiConnection = async () => {
        if (this.probing) {
            return this.apiOnline;
        }

        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            this.forceOffline();
            return false;
        }

        if (!API_BASE_URL) {
            this.registerReachabilitySuccess();
            return true;
        }

        const token = localStorage.getItem('visor-access-token');
        const deviceUUID = localStorage.getItem('device_uuid');

        if (!token) {
            runInAction(() => {
                this.browserOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
                this.apiOnline = this.browserOnline;
                this.consecutiveConnectivityFailures = 0;
                this.lastCheckedAt = new Date().toISOString();
            });
            return this.apiOnline;
        }

        this.probing = true;

        try {
            await axios.get(api.ping, {
                timeout: API_PING_TIMEOUT_MS,
                validateStatus: () => true,
                __networkProbe: true,
                // headers: {
                //     Authorization: `Bearer ${token}`,
                //     ...(deviceUUID ? {'Device-UUID': deviceUUID} : {}),
                // },
            } as any);

            this.registerReachabilitySuccess();
            return true;
        } catch (error) {
            if (isConnectivityError(error)) {
                this.registerReachabilityFailure();
                return this.apiOnline;
            }

            this.registerReachabilitySuccess();
            return true;
        } finally {
            this.probing = false;
        }
    };

    private handleBrowserOnline = () => {
        runInAction(() => {
            this.browserOnline = true;
        });

        this.clearPendingOnlineTimer();
        void this.checkApiConnection();
    };

    private handleBrowserOffline = () => {
        this.forceOffline();
    };

    private registerReachabilitySuccess = () => {
        this.consecutiveConnectivityFailures = 0;

        if (this.apiOnline) {
            this.markOnline();
            return;
        }

        if (this.pendingOnlineTimer !== null) {
            return;
        }

        if (typeof window === 'undefined') {
            this.markOnline();
            return;
        }

        this.pendingOnlineTimer = window.setTimeout(() => {
            this.pendingOnlineTimer = null;

            if (this.browserOnline && this.consecutiveConnectivityFailures === 0) {
                this.markOnline();
            }
        }, API_RECOVERY_DELAY_MS);
    };

    private registerReachabilityFailure = () => {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            this.forceOffline();
            return;
        }

        this.clearPendingOnlineTimer();
        this.consecutiveConnectivityFailures += 1;

        if (this.consecutiveConnectivityFailures < API_FAILURES_BEFORE_OFFLINE) {
            runInAction(() => {
                this.lastCheckedAt = new Date().toISOString();
            });
            return;
        }

        this.markOffline();
    };

    private markOnline = () => {
        this.clearPendingOnlineTimer();
        runInAction(() => {
            this.browserOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
            this.apiOnline = true;
            this.consecutiveConnectivityFailures = 0;
            this.lastCheckedAt = new Date().toISOString();
        });
    };

    private markOffline = () => {
        this.clearPendingOnlineTimer();
        runInAction(() => {
            this.browserOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
            this.apiOnline = false;
            this.lastCheckedAt = new Date().toISOString();
        });
    };

    private forceOffline = () => {
        this.clearPendingOnlineTimer();
        runInAction(() => {
            this.browserOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
            this.apiOnline = false;
            this.consecutiveConnectivityFailures = API_FAILURES_BEFORE_OFFLINE;
            this.lastCheckedAt = new Date().toISOString();
        });
    };

    private clearPendingOnlineTimer = () => {
        if (typeof window === 'undefined' || this.pendingOnlineTimer === null) {
            return;
        }

        window.clearTimeout(this.pendingOnlineTimer);
        this.pendingOnlineTimer = null;
    };
}

export default new NetworkStore();
